import { useCallback, useMemo } from 'react';
import type { NotificationPermissionsStatus } from 'expo-notifications';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe-features-permissions';

import { useTelemetry } from '@order/Telemetry';

import { usePushNotificationPermissionPrimerEnabled } from '../usePushNotificationPermissionPrimerEnabled';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the push notification permission primer flow shown from the location
 * search tab.
 *
 * Returns the flags + handlers the consumer needs to decide whether to render
 * a loading screen, the primer, or its normal content.
 */
export const usePushNotificationPermissionPrimer = () => {
  const { track } = useTelemetry();
  const { canEnablePushNotificationPrimer: isPushNotificationPrimerEnabled } =
    usePushNotificationPermissionPrimerEnabled();

  const onPushNotificationPrimerShow = useCallback(() => {
    track('push_notification_primer_shown', PUSH_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onRequestPushNotificationPermission = useCallback(() => {
    track('push_notification_primer_cta_tapped', PUSH_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onSkipPushNotificationPermission = useCallback(() => {
    track('push_notification_primer_dismissed', {
      ...PUSH_PRIMER_TELEMETRY_PAYLOAD,
      action: 'not_now',
    });
  }, [track]);

  const onPushNotificationPermissionGranted = useCallback(
    (_args: unknown, _permissionResponse: NotificationPermissionsStatus) => {
      track('push_permission_granted', PUSH_PRIMER_TELEMETRY_PAYLOAD);
    },
    [track],
  );

  const onPushNotificationPermissionDenied = useCallback(
    (_args: unknown, _permissionResponse?: NotificationPermissionsStatus) => {
      track('push_permission_denied', PUSH_PRIMER_TELEMETRY_PAYLOAD);
    },
    [track],
  );

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: {
        onPushNotificationPrimerShow,
        onRequestPushNotificationPermission,
        onSkipPushNotificationPermission,
        onPushNotificationPermissionGranted,
        onPushNotificationPermissionDenied,
      },
    }),
    [
      onPushNotificationPermissionDenied,
      onPushNotificationPermissionGranted,
      onPushNotificationPrimerShow,
      onRequestPushNotificationPermission,
      onSkipPushNotificationPermission,
    ],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isPushNotificationPrimerEnabled,
    implementations,
  });

  const {
    isPushNotificationPermissionsLoading,
    isPushNotificationPrimerVisible,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    requestPushNotificationPermissions,
    skipPushNotificationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isPushNotificationPrimerVisible ||
    isRequestingPushNotificationPermission ||
    isSkippingPushNotificationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isPushNotificationPrimerEnabled,
    isPushNotificationPermissionsLoading,
    isPrimerActive,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    onAllow: requestPushNotificationPermissions,
    onSkip: skipPushNotificationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PUSH_PRIMER_TELEMETRY_PAYLOAD = { entry_point: 'location-search' };
